import { gsap } from "gsap";

/**
 * Site navigation.
 *
 * Desktop (>=1024px): top-level items with children open a small text dropdown
 *   on hover, and on click/Enter for keyboard and touch users.
 * Mobile (<1024px): the whole nav is a slide-in drawer behind a hamburger, and
 *   the same panels become inline accordions.
 *
 * Panels are shown/hidden via the `hidden` attribute so the closed state is
 * real to assistive tech, not just visually offscreen.
 *
 * No-ops when the markup isn't present.
 */

const DESKTOP = "(min-width: 1024px)";
const HOVER = "(hover: hover) and (pointer: fine)";

function initNav() {
  const nav = document.querySelector("[data-nav]");
  const toggle = document.querySelector("[data-nav-toggle]");
  if (!nav) return;

  const triggers = Array.from(nav.querySelectorAll("[data-nav-trigger]"));
  const expanders = Array.from(nav.querySelectorAll("[data-nav-expand]"));
  const desktopQuery = window.matchMedia(DESKTOP);
  const hoverQuery = window.matchMedia(HOVER);

  const isDesktop = () => desktopQuery.matches;

  // Backdrop behind the mobile drawer. Created once, reused.
  let backdrop = document.querySelector(".nav-backdrop");
  if (!backdrop) {
    backdrop = document.createElement("div");
    backdrop.className = "nav-backdrop";
    document.body.appendChild(backdrop);
  }

  /* Panels ---------------------------------------------------- */

  const panelFor = (trigger) =>
    nav.querySelector(`[data-nav-panel="${trigger.dataset.navTrigger}"]`);

  const reduceMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;

  // The chevron BUTTON owns aria-expanded, since it is the control that opens
  // the panel. The label beside it is a plain link to the parent page.
  const expanderFor = (trigger) =>
    nav.querySelector(`[data-nav-expand="${trigger.dataset.navTrigger}"]`);

  function openPanel(trigger) {
    const panel = panelFor(trigger);
    if (!panel) return;
    expanderFor(trigger)?.setAttribute("aria-expanded", "true");

    // `hidden` must come off BEFORE the tween: a display:none element has no
    // box to animate, so GSAP would have nothing to work with.
    panel.hidden = false;
    gsap.killTweensOf(panel);

    if (reduceMotion) {
      gsap.set(panel, { autoAlpha: 1, y: 0 });
      return;
    }

    gsap.fromTo(
      panel,
      { autoAlpha: 0, y: -8 },
      { autoAlpha: 1, y: 0, duration: 0.28, ease: "power2.out" },
    );
  }

  function closePanel(trigger) {
    const panel = panelFor(trigger);
    if (!panel) return;
    expanderFor(trigger)?.setAttribute("aria-expanded", "false");

    gsap.killTweensOf(panel);

    // `hidden` goes back on only once the fade-out finishes, so the panel is
    // still rendered while it animates but remains hidden from assistive tech
    // and out of the tab order when at rest.
    if (reduceMotion || panel.hidden) {
      panel.hidden = true;
      return;
    }

    gsap.to(panel, {
      autoAlpha: 0,
      y: -8,
      duration: 0.2,
      ease: "power2.in",
      onComplete: () => {
        panel.hidden = true;
      },
    });
  }

  function closeAllPanels(except = null) {
    triggers.forEach((t) => {
      if (t !== except) closePanel(t);
    });
  }

  triggers.forEach((trigger) => {
    const item = trigger.closest(".nav__item");

    // The label is now a plain link — it always navigates. Opening the panel is
    // the chevron button's job (see the expanders loop below), which is what
    // makes the menu keyboard-operable.

    // Hover, desktop pointer devices only. Touch devices report no hover, so
    // they fall through to the click handler above and don't double-fire.
    item?.addEventListener("mouseenter", () => {
      if (!isDesktop() || !hoverQuery.matches) return;
      closeAllPanels(trigger);
      openPanel(trigger);
    });

    item?.addEventListener("mouseleave", () => {
      if (!isDesktop() || !hoverQuery.matches) return;
      closePanel(trigger);
    });
  });

  /* Chevron buttons — the keyboard-accessible way to open a panel ---- */

  expanders.forEach((expander) => {
    const trigger = triggers.find(
      (t) => t.dataset.navTrigger === expander.dataset.navExpand,
    );
    if (!trigger) return;

    // Enter/Space fire click on a <button>, so this covers pointer and keyboard
    // alike, at every breakpoint.
    expander.addEventListener("click", () => {
      const isOpen = expander.getAttribute("aria-expanded") === "true";
      if (isOpen) {
        closePanel(trigger);
      } else {
        // Desktop shows one panel at a time; the drawer is an accordion where
        // several may sit open at once.
        if (isDesktop()) closeAllPanels(trigger);
        openPanel(trigger);
      }
    });

    // Escape closes and returns focus to the chevron that opened it.
    expander.addEventListener("keydown", (e) => {
      if (e.key !== "Escape") return;
      closePanel(trigger);
      expander.focus();
    });

    // Tabbing forward off the chevron moves INTO the open panel, so the
    // submenu is reached in reading order rather than being skipped.
    expander.addEventListener("keydown", (e) => {
      if (e.key !== "Tab" || e.shiftKey || !isDesktop()) return;
      if (expander.getAttribute("aria-expanded") !== "true") return;

      const first = panelFor(trigger)?.querySelector("a");
      if (!first) return;
      e.preventDefault();
      first.focus();
    });
  });

  /* Panel links — close on tabbing out of the end -------------------- */

  triggers.forEach((trigger) => {
    const panel = panelFor(trigger);
    if (!panel) return;
    const links = Array.from(panel.querySelectorAll("a"));
    if (!links.length) return;

    links.forEach((link, i) => {
      link.addEventListener("keydown", (e) => {
        if (e.key !== "Tab" || !isDesktop()) return;

        // Past the LAST link: close the panel and let focus continue on to the
        // next top-level item, so the menu never traps the keyboard.
        if (!e.shiftKey && i === links.length - 1) {
          closePanel(trigger);
          return; // no preventDefault — the browser's own Tab order takes over
        }

        // Shift+Tab off the FIRST link returns to the chevron and closes,
        // mirroring the forward path.
        if (e.shiftKey && i === 0) {
          e.preventDefault();
          closePanel(trigger);
          expanderFor(trigger)?.focus();
        }
      });
    });
  });

  /* Mobile drawer --------------------------------------------- */

  /* Page scroll lock.

     `overflow: hidden` on <html>/<body> is not enough on its own — Chrome (and
     iOS Safari) still scroll the page behind the open menu. The reliable lock
     is to take the body out of flow with position:fixed, which cannot scroll
     at all.

     Doing that collapses the page to the top, so the current offset is stored
     and re-applied as a negative top, keeping the view visually still. Closing
     restores the offset. */
  let scrollLockY = 0;

  function lockScroll() {
    scrollLockY = window.scrollY;
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollLockY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    // Holds the scrollbar gutter so the page does not shift sideways.
    document.body.style.width = "100%";
  }

  function unlockScroll() {
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.left = "";
    document.body.style.right = "";
    document.body.style.width = "";
    // `auto` — the restore must be instant, never animated by a smooth-scroll
    // setting, or the page visibly races back to position.
    window.scrollTo({ top: scrollLockY, behavior: "auto" });
  }

  function openDrawer() {
    nav.classList.add("is-open");
    backdrop.classList.add("is-visible");
    document.body.classList.add("nav-open");
    lockScroll();
    toggle?.setAttribute("aria-expanded", "true");
    toggle?.setAttribute("aria-label", "Close menu");
  }

  function closeDrawer() {
    // Guard: closeDrawer also runs on link clicks, Escape and resize, and
    // unlocking when nothing is locked would scroll the page to a stale offset.
    const wasOpen = nav.classList.contains("is-open");

    nav.classList.remove("is-open");
    backdrop.classList.remove("is-visible");
    document.body.classList.remove("nav-open");
    if (wasOpen) unlockScroll();
    toggle?.setAttribute("aria-expanded", "false");
    toggle?.setAttribute("aria-label", "Open menu");
  }

  const drawerIsOpen = () => nav.classList.contains("is-open");

  toggle?.addEventListener("click", () => {
    drawerIsOpen() ? closeDrawer() : openDrawer();
  });

  backdrop.addEventListener("click", closeDrawer);

  // Following a link inside the drawer should close it — the new page renders
  // behind an open drawer otherwise.
  nav.querySelectorAll("a").forEach((link) => {
    link.addEventListener("click", () => {
      if (!isDesktop()) closeDrawer();
    });
  });

  /* Global dismissal ------------------------------------------ */

  document.addEventListener("click", (e) => {
    if (nav.contains(e.target) || toggle?.contains(e.target)) return;
    closeAllPanels();
    if (!isDesktop()) closeDrawer();
  });

  document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape") return;
    closeAllPanels();
    if (drawerIsOpen()) {
      closeDrawer();
      toggle?.focus();
    }
  });

  // Crossing the breakpoint: reset to that layout's resting state, so a drawer
  // left open on mobile doesn't linger as a stuck panel on desktop.
  desktopQuery.addEventListener("change", () => {
    closeAllPanels();
    closeDrawer();
  });

  /* Cursor-following border glow ------------------------------- */

  // A full inset ring is drawn on an injected layer, then a radial MASK reveals
  // only the part of it near the cursor. Moving the mask's centre is what makes
  // the glow follow the pointer — the ring itself never moves.
  //
  // Ported from the service-card effect on thelonelypixel.co.uk.
  const glowEnabled = !reduceMotion && hoverQuery.matches;

  initGlowBorder(document.querySelector(".header__bar"), {
    enabled: glowEnabled,
  });

  // Same treatment on the dropdown panels. Safe to run now even though they are
  // hidden: the listeners bind regardless, and the guard inside means a panel
  // is only ever initialised once.
  // Wider/shorter radii than the bar's: a panel is tall and narrow, so the axes
  // are effectively swapped relative to the header.
  nav.querySelectorAll("[data-nav-panel]").forEach((panel) => {
    initGlowBorder(panel, {
      enabled: glowEnabled,
      radiusX: "70%",
      radiusY: "35%",
    });
  });
}

function initGlowBorder(
  target,
  { enabled = true, radiusX = "30%", radiusY = "62%" } = {},
) {
  if (!target || !enabled) return;
  if (target.hasAttribute("data-glow-initialized")) return;
  target.setAttribute("data-glow-initialized", "");

  // An ELLIPSE, not a circle, and deliberately short vertically.
  //
  // A circle on a wide, short bar is dominated by horizontal distance: the
  // whole height falls inside the bright stop, so moving the cursor up or down
  // barely changes anything. Sizing the mask to ~55% of the bar's height means
  // the vertical axis actually does the work — with the cursor at the bottom
  // the top edge falls outside the mask entirely, and vice versa.
  //
  // Midway between the two extremes tried: 10%/55% was so tight that the middle
  // of the bar revealed almost nothing, while 45%/95% lost the directional
  // falloff. These stops keep a visible core with a clear vertical bias.
  //
  // The radii are percentages of the TARGET's own box, so they are passed in:
  // the header bar is wide and short, a dropdown panel is the opposite, and one
  // fixed pair of values cannot suit both.
  const mask = (x, y) =>
    `radial-gradient(ellipse ${radiusX} ${radiusY} at ${x}% ${y}%, white 28%, transparent 75%)`;

  const glow = document.createElement("div");
  glow.className = "glow-border";
  Object.assign(glow.style, {
    position: "absolute",
    inset: "0",
    borderRadius: "inherit",
    pointerEvents: "none",
    // Above the ::after border ring (z-index 1) so the glow reads on top.
    zIndex: "2",
    opacity: "0",
    transition: "opacity 0.3s",
    boxSizing: "border-box",
    // The ring itself: a 1px inset shadow, so it traces the rounded corners
    // exactly without needing its own border.
    boxShadow: "inset 0 0 0 1px rgba(255, 255, 255, 0.9)",
    maskImage: mask(50, 50),
    WebkitMaskImage: mask(50, 50),
    maskRepeat: "no-repeat",
    WebkitMaskRepeat: "no-repeat",
    maskSize: "100% 100%",
    WebkitMaskSize: "100% 100%",
  });
  target.appendChild(glow);

  target.addEventListener("mouseenter", () => {
    glow.style.opacity = "1";
  });

  target.addEventListener("mouseleave", () => {
    glow.style.opacity = "0";
  });

  target.addEventListener("mousemove", (e) => {
    const rect = target.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    const next = mask(x, y);

    // Tweened rather than set directly, so the glow eases toward the cursor
    // instead of snapping to it.
    gsap.to(glow, {
      WebkitMaskImage: next,
      maskImage: next,
      duration: 0.3,
      ease: "power2.out",
    });
  });
}

export { initNav };
